import { useEffect, useState } from "react";
import { apiClient, ApiError } from "../../lib/api-client";
import { usePagination } from "../../hooks/usePagination";
import { Pagination } from "../../components/Pagination";

interface AdminCategoryItem {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  parent_name: string | null;
  created_at: string;
  books_count: number;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function AdminCategoriesPage() {
  const [categories, setCategories] = useState<AdminCategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  const pagination = usePagination(categories, 15);

  // Formulário de Criação
  const [createForm, setCreateForm] = useState({
    name: "",
    slug: "",
    parentId: "" as string,
  });

  // Modal de Edição
  const [editingCategory, setEditingCategory] = useState<AdminCategoryItem | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    slug: "",
    parentId: "" as string,
  });

  function loadData() {
    setLoading(true);
    apiClient
      .get<{ items: AdminCategoryItem[] }>("/admin/categories")
      .then((res) => {
        setCategories(res.items || []);
      })
      .catch((err) => {
        setStatusMessage({
          text: err instanceof ApiError ? err.message : "Erro ao carregar categorias.",
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadData();
  }, []);

  function handleNameChange(newName: string) {
    const generatedSlug = slugify(newName);
    setCreateForm((prev) => ({
      ...prev,
      name: newName,
      slug: prev.slug === "" || prev.slug === slugify(prev.name) ? generatedSlug : prev.slug,
    }));
  }

  async function handleCreateCategory(e: React.FormEvent) {
    e.preventDefault();
    setStatusMessage(null);

    try {
      await apiClient.post("/admin/categories", {
        name: createForm.name.trim(),
        slug: createForm.slug.trim() || undefined,
        parentId: createForm.parentId ? createForm.parentId : null,
      });

      setStatusMessage({ text: `Categoria "${createForm.name}" cadastrada com sucesso!`, type: "success" });
      setShowCreateForm(false);
      setCreateForm({ name: "", slug: "", parentId: "" });
      loadData();
    } catch (err) {
      setStatusMessage({
        text: err instanceof ApiError ? err.message : "Falha ao criar categoria.",
        type: "error",
      });
    }
  }

  function startEdit(cat: AdminCategoryItem) {
    setEditingCategory(cat);
    setEditForm({
      name: cat.name,
      slug: cat.slug,
      parentId: cat.parent_id || "",
    });
  }

  async function handleUpdateCategory(e: React.FormEvent) {
    e.preventDefault();
    if (!editingCategory) return;
    setStatusMessage(null);

    try {
      await apiClient.patch(`/admin/categories/${editingCategory.id}`, {
        name: editForm.name.trim(),
        slug: editForm.slug.trim(),
        parentId: editForm.parentId ? editForm.parentId : null,
      });

      setStatusMessage({ text: `Categoria "${editForm.name}" atualizada com sucesso!`, type: "success" });
      setEditingCategory(null);
      loadData();
    } catch (err) {
      setStatusMessage({
        text: err instanceof ApiError ? err.message : "Falha ao atualizar categoria.",
        type: "error",
      });
    }
  }

  async function handleDeleteCategory(cat: AdminCategoryItem) {
    const confirmDelete = window.confirm(
      `Deseja realmente excluir a categoria temática "${cat.name}"?\n\nOs livros associados permanecerão no catálogo com a categoria desvinculada.`
    );
    if (!confirmDelete) return;

    try {
      await apiClient.delete(`/admin/categories/${cat.id}`);
      setStatusMessage({ text: `Categoria "${cat.name}" excluída.`, type: "success" });
      loadData();
    } catch (err) {
      setStatusMessage({
        text: err instanceof ApiError ? err.message : "Falha ao excluir categoria.",
        type: "error",
      });
    }
  }

  // Contadores
  const totalCategories = categories.length;
  const categoriesWithBooks = categories.filter((c) => c.books_count > 0).length;
  const mainCategories = categories.filter((c) => !c.parent_id).length;
  const subCategories = categories.filter((c) => Boolean(c.parent_id)).length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "var(--space-4)" }}>
        <div>
          <h2 style={{ marginBottom: "var(--space-1)" }}>Categorias Temáticas do Catálogo</h2>
          <p className="text-muted" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
            Estruture o acervo editorial da Datasci em áreas do conhecimento (Engenharia de Dados, Machine Learning, Estatística, etc.).
          </p>
        </div>
        <button
          className="btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{ whiteSpace: "nowrap" }}
        >
          {showCreateForm ? "Fechar Formulário" : "+ Nova Categoria"}
        </button>
      </div>

      {statusMessage && (
        <div
          className="card"
          style={{
            padding: "var(--space-3) var(--space-4)",
            marginBottom: "var(--space-4)",
            backgroundColor: statusMessage.type === "success" ? "rgba(46, 125, 50, 0.1)" : "rgba(211, 47, 47, 0.1)",
            borderLeft: `4px solid ${statusMessage.type === "success" ? "#2e7d32" : "#d32f2f"}`,
            color: statusMessage.type === "success" ? "#1b5e20" : "#c62828",
            fontSize: "var(--text-sm)",
          }}
        >
          {statusMessage.text}
        </div>
      )}

      {/* Cards de Métricas */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "var(--space-3)",
          marginBottom: "var(--space-5)",
        }}
      >
        <div className="card" style={{ padding: "var(--space-3)" }}>
          <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Total de Categorias</div>
          <div style={{ fontSize: "var(--text-2xl)", fontWeight: 700 }}>{totalCategories}</div>
        </div>
        <div className="card" style={{ padding: "var(--space-3)" }}>
          <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Categorias com Livros</div>
          <div style={{ fontSize: "var(--text-2xl)", fontWeight: 700, color: "var(--color-primary, #0066cc)" }}>
            {categoriesWithBooks}
          </div>
        </div>
        <div className="card" style={{ padding: "var(--space-3)" }}>
          <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Categorias Principais</div>
          <div style={{ fontSize: "var(--text-2xl)", fontWeight: 700, color: "#15803d" }}>
            {mainCategories}
          </div>
        </div>
        <div className="card" style={{ padding: "var(--space-3)" }}>
          <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Subcategorias</div>
          <div style={{ fontSize: "var(--text-2xl)", fontWeight: 700, color: "#6b21a8" }}>
            {subCategories}
          </div>
        </div>
      </div>

      {/* Formulário de Criação de Categoria */}
      {showCreateForm && (
        <form onSubmit={handleCreateCategory} className="card" style={{ marginBottom: "var(--space-6)", padding: "var(--space-5)" }}>
          <h3 style={{ marginTop: 0, marginBottom: "var(--space-3)" }}>Cadastrar Nova Categoria Temática</h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "var(--space-4)" }}>
            <div className="field">
              <label htmlFor="create-cat-name">Nome da Categoria *</label>
              <input
                id="create-cat-name"
                required
                placeholder="Ex: Computação Quântica"
                value={createForm.name}
                onChange={(e) => handleNameChange(e.target.value)}
                autoFocus
              />
            </div>

            <div className="field">
              <label htmlFor="create-cat-slug">Slug (URL amigável) *</label>
              <input
                id="create-cat-slug"
                required
                placeholder="computacao-quantica"
                value={createForm.slug}
                onChange={(e) => setCreateForm({ ...createForm, slug: slugify(e.target.value) })}
              />
              <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
                URL no catálogo público: <code>/catalogo?categoria={createForm.slug || "slug"}</code>
              </span>
            </div>

            <div className="field">
              <label htmlFor="create-cat-parent">Categoria Pai (Opcional)</label>
              <select
                id="create-cat-parent"
                value={createForm.parentId}
                onChange={(e) => setCreateForm({ ...createForm, parentId: e.target.value })}
              >
                <option value="">(Nenhuma - Categoria Principal)</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end", marginTop: "var(--space-4)" }}>
            <button type="button" className="btn-secondary" onClick={() => setShowCreateForm(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn">
              Salvar Categoria
            </button>
          </div>
        </form>
      )}

      {/* Tabela de Categorias */}
      {loading ? (
        <p className="text-muted">Carregando categorias temáticas...</p>
      ) : categories.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "var(--space-8)" }}>
          <p className="text-muted" style={{ marginBottom: "var(--space-3)" }}>
            Nenhuma categoria temática cadastrada até o momento.
          </p>
          <button className="btn" onClick={() => setShowCreateForm(true)}>
            Cadastrar Primeira Categoria
          </button>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="table" style={{ width: "100%", fontSize: "var(--text-sm)" }}>
            <thead>
              <tr>
                <th>Nome da Categoria</th>
                <th>Slug</th>
                <th>Hierarquia</th>
                <th>Obras no Acervo</th>
                <th style={{ textAlign: "right" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pagination.paginatedItems.map((cat) => (
                <tr key={cat.id}>
                  <td>
                    <strong style={{ fontSize: "var(--text-base)" }}>{cat.name}</strong>
                  </td>
                  <td>
                    <code>{cat.slug}</code>
                  </td>
                  <td>
                    {cat.parent_name ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "2px 8px",
                          borderRadius: 4,
                          background: "rgba(107, 33, 168, 0.1)",
                          color: "#6b21a8",
                          fontSize: "var(--text-xs)",
                          fontWeight: 500,
                        }}
                      >
                        Subcategoria de: {cat.parent_name}
                      </span>
                    ) : (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "2px 8px",
                          borderRadius: 4,
                          background: "rgba(34, 197, 94, 0.1)",
                          color: "#15803d",
                          fontSize: "var(--text-xs)",
                          fontWeight: 500,
                        }}
                      >
                        Principal
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 12,
                        background: "#f1f5f9",
                        fontWeight: 600,
                      }}
                    >
                      {cat.books_count} {cat.books_count === 1 ? "título" : "títulos"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: "var(--text-xs)", padding: "4px 8px", marginRight: 6 }}
                      onClick={() => startEdit(cat)}
                    >
                      Editar
                    </button>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: "var(--text-xs)", padding: "4px 8px", color: "#dc2626" }}
                      onClick={() => handleDeleteCategory(cat)}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={pagination.currentPage}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={pagination.setCurrentPage}
            itemLabel="categorias"
          />
        </div>
      )}

      {/* Modal de Edição de Categoria */}
      {editingCategory && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "var(--space-4)",
          }}
          onClick={() => setEditingCategory(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 520,
              width: "100%",
              padding: "var(--space-5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-4)" }}>
              <h3 style={{ margin: 0 }}>Editar Categoria: {editingCategory.name}</h3>
              <button
                type="button"
                className="btn-secondary"
                style={{ fontSize: "var(--text-xs)", padding: "2px 8px" }}
                onClick={() => setEditingCategory(null)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateCategory}>
              <div className="field">
                <label htmlFor="edit-cat-name">Nome da Categoria *</label>
                <input
                  id="edit-cat-name"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>

              <div className="field" style={{ marginTop: "var(--space-3)" }}>
                <label htmlFor="edit-cat-slug">Slug *</label>
                <input
                  id="edit-cat-slug"
                  required
                  value={editForm.slug}
                  onChange={(e) => setEditForm({ ...editForm, slug: slugify(e.target.value) })}
                />
              </div>

              <div className="field" style={{ marginTop: "var(--space-3)" }}>
                <label htmlFor="edit-cat-parent">Categoria Pai</label>
                <select
                  id="edit-cat-parent"
                  value={editForm.parentId}
                  onChange={(e) => setEditForm({ ...editForm, parentId: e.target.value })}
                >
                  <option value="">(Nenhuma - Categoria Principal)</option>
                  {categories
                    .filter((c) => c.id !== editingCategory.id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </div>

              <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end", marginTop: "var(--space-5)" }}>
                <button type="button" className="btn-secondary" onClick={() => setEditingCategory(null)}>
                  Cancelar
                </button>
                <button type="submit" className="btn">
                  Salvar Alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
